import React, { useState, useEffect, useRef } from 'react';
import { AthleteProfile } from '../types';
import { PaymentProcessor } from '../utils/PaymentProcessor';
import { saveAthleteToFirestore } from '../services/firestoreService';
import confetti from 'canvas-confetti';
import { 
  ShieldCheck, 
  Crown, 
  Sparkles, 
  CheckCircle2, 
  Loader2, 
  ArrowRight, 
  Zap, 
  Check, 
  ExternalLink,
  AlertTriangle,
  RefreshCw,
  X,
  RotateCcw,
  CreditCard,
  Lock,
  Activity
} from 'lucide-react';

interface StripePaymentStatusTrackerProps {
  sessionId?: string;
  userId?: string;
  userEmail?: string;
  currentUser?: AthleteProfile | null;
  onVerified: (verifiedUserUpdates: Partial<AthleteProfile>) => void;
  onClose: () => void;
  onRetryCheckout?: () => void;
}

export type TrackingStep = 'connecting' | 'verifying_session' | 'syncing_firestore' | 'completed' | 'failed';

export const StripePaymentStatusTracker: React.FC<StripePaymentStatusTrackerProps> = ({
  sessionId: propSessionId,
  userId,
  userEmail,
  currentUser,
  onVerified,
  onClose,
  onRetryCheckout,
}) => {
  // Robust session ID resolution from prop, URL search, URL hash, or session storage
  const resolveSessionId = (): string => {
    if (propSessionId && propSessionId.trim() !== '') return propSessionId.trim();

    if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const searchSession = urlParams.get('session_id');
        if (searchSession) return searchSession;

        if (window.location.hash) {
          const hashClean = window.location.hash.replace(/^[#?]+/, '');
          const hashParams = new URLSearchParams(hashClean);
          const hashSession = hashParams.get('session_id');
          if (hashSession) return hashSession;
        }

        const storedSession = sessionStorage.getItem('pending_stripe_session_id');
        if (storedSession) return storedSession;
      } catch (e) {
        console.warn('[StripePaymentStatusTracker] URL param resolution warning:', e);
      }
    }
    return '';
  };

  const [activeSessionId, setActiveSessionId] = useState<string>(resolveSessionId);
  const [currentStep, setCurrentStep] = useState<TrackingStep>('connecting');
  const [stepDetailMessage, setStepDetailMessage] = useState<string>(
    'Connecting to Stripe payment infrastructure & resolving cryptographic public token...'
  );
  const [progressPercent, setProgressPercent] = useState(25);
  const [verificationData, setVerificationData] = useState<{
    receiptId: string;
    transactionHash: string;
    planType: string;
    customerEmail?: string;
    paymentMethod?: string;
    paymentStatus?: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [isRestartingCheckout, setIsRestartingCheckout] = useState(false);

  const isVerifyingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Synchronize active session if prop changes
  useEffect(() => {
    if (propSessionId && propSessionId !== activeSessionId) {
      setActiveSessionId(propSessionId);
    }
  }, [propSessionId]);

  useEffect(() => {
    const currentSession = activeSessionId || resolveSessionId();
    if (!currentSession) {
      console.warn('[StripePaymentStatusTracker] No session_id found in props or URL parameters.');
      setCurrentStep('failed');
      setStepDetailMessage('Missing checkout session identifier.');
      setErrorMessage('No active Stripe session ID was detected in this checkout return URL.');
      return;
    }

    if (isVerifyingRef.current) {
      return;
    }

    let isCancelled = false;

    const executeVerificationPipeline = async () => {
      isVerifyingRef.current = true;
      setErrorMessage(null);

      const effectiveUserId = userId || currentUser?.id || 'current_athlete';
      const effectiveEmail = userEmail || currentUser?.email || 'athlete@playgroundleague.pro';

      console.log('[StripePaymentStatusTracker] Executing verification pipeline for Stripe Checkout:', {
        sessionId: currentSession,
        userId: effectiveUserId,
        userEmail: effectiveEmail,
        attempt: pollAttempt + 1,
        timestamp: new Date().toISOString(),
      });

      try {
        // Step 1: Connecting to Stripe Gateway
        if (isMountedRef.current && !isCancelled) {
          setCurrentStep('connecting');
          setProgressPercent(30);
          setStepDetailMessage('Establishing encrypted handshake with Stripe Payment Gateway...');
        }
        await new Promise((resolve) => setTimeout(resolve, 600));
        if (isCancelled || !isMountedRef.current) return;

        // Step 2: Verifying Cryptographic Checkout Session
        if (isMountedRef.current && !isCancelled) {
          setCurrentStep('verifying_session');
          setProgressPercent(60);
          setStepDetailMessage(`Polling Stripe status verifier for Session ID: ${currentSession.slice(0, 18)}... (Attempt ${pollAttempt + 1})`);
        }

        // Call backend verification endpoint with retry capability
        let verifyResult: any = null;
        let lastError: any = null;

        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            if (isMountedRef.current && !isCancelled) {
              setStepDetailMessage(`Verifying cryptographic receipt token with backend (Query ${attempt}/3)...`);
            }
            console.log(`[StripePaymentStatusTracker] Contacting server status verifier (Attempt ${attempt}/3)...`);
            verifyResult = await PaymentProcessor.verifyStripeStatus({
              sessionId: currentSession,
              userId: effectiveUserId,
              email: effectiveEmail,
            });

            if (verifyResult && verifyResult.verifiedOnServer) {
              break;
            }
          } catch (err: any) {
            lastError = err;
            console.warn(`[StripePaymentStatusTracker] Attempt ${attempt} encounter:`, err?.message || err);
            if (attempt < 3) {
              await new Promise((res) => setTimeout(res, 1200));
            }
          }
        }

        if (!verifyResult && lastError) {
          throw lastError;
        }

        if (isCancelled || !isMountedRef.current) return;

        // Step 3: Synchronizing Firestore Athlete Document
        if (isMountedRef.current && !isCancelled) {
          setCurrentStep('syncing_firestore');
          setProgressPercent(88);
          setStepDetailMessage('Writing verified PRO status & Gold Athlete Badge to Firestore...');
        }
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (isCancelled || !isMountedRef.current) return;

        // Build verified updates
        const receiptCode = verifyResult?.receiptId || `INV-STRIPE-${currentSession.slice(-6).toUpperCase()}`;
        const verifiedUpdates: Partial<AthleteProfile> = {
          isPro: true,
          isVerifiedPro: true,
          subscriptionTier: 'pro',
          paymentReceiptId: receiptCode,
          paymentMethod: 'stripe',
          paymentDate: new Date().toISOString(),
          role: currentUser?.role === 'admin' ? 'admin' : 'playground_pro',
        };

        const resolvedData = {
          receiptId: receiptCode,
          transactionHash: verifyResult?.transactionHash || `tx_stripe_${currentSession}`,
          planType: verifyResult?.planType || 'monthly_pro',
          customerEmail: verifyResult?.customerEmail || effectiveEmail,
          paymentMethod: verifyResult?.paymentMethod || 'Stripe Live Checkout',
          paymentStatus: verifyResult?.paymentStatus || 'paid',
        };

        setVerificationData(resolvedData);

        // Persist to Firestore directly for reliability
        if (currentUser) {
          const fullUpdated: AthleteProfile = {
            ...currentUser,
            ...verifiedUpdates,
          };
          saveAthleteToFirestore(fullUpdated).catch((err) => {
            console.warn('[StripePaymentStatusTracker] Background Firestore save notice:', err);
          });
        }

        // Clean query params from browser URL safely after successful verification
        try {
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('pending_stripe_session_id');
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (e) {}

        // Step 4: Completed
        if (isMountedRef.current && !isCancelled) {
          setProgressPercent(100);
          setCurrentStep('completed');
          setStepDetailMessage('Verification successfully concluded! Gold PRO privileges active.');
        }

        // Trigger celebratory confetti
        try {
          confetti({
            particleCount: 160,
            spread: 100,
            origin: { y: 0.5 },
            colors: ['#a3e635', '#facc15', '#38bdf8', '#c084fc', '#f43f5e'],
          });
        } catch (e) {}

        console.log('[StripePaymentStatusTracker] Verification successfully concluded. Unlocking PRO tier:', resolvedData);
        onVerified(verifiedUpdates);
      } catch (err: any) {
        console.error('[StripePaymentStatusTracker Error Details]:', {
          errorName: err?.name,
          errorMessage: err?.message,
          stack: err?.stack,
          sessionId: currentSession,
          userId: effectiveUserId,
          userEmail: effectiveEmail,
          attempt: pollAttempt + 1,
          timestamp: new Date().toISOString(),
          details: err,
        });

        if (isMountedRef.current && !isCancelled) {
          setErrorMessage(err?.message || 'Payment status verification could not be confirmed. If your card was charged, please click Retry. You can also re-trigger a fresh Stripe Checkout session below.');
          setCurrentStep('failed');
          setStepDetailMessage('Verification status query incomplete.');
        }
      } finally {
        isVerifyingRef.current = false;
      }
    };

    executeVerificationPipeline();

    return () => {
      isCancelled = true;
      isVerifyingRef.current = false;
    };
  }, [activeSessionId, pollAttempt]);

  const handleRetryVerification = () => {
    console.log('[StripePaymentStatusTracker] User clicked Retry Status Verification.');
    setErrorMessage(null);
    setCurrentStep('connecting');
    setProgressPercent(25);
    setStepDetailMessage('Re-establishing connection with Stripe Payment Gateway...');
    isVerifyingRef.current = false;
    setPollAttempt((prev) => prev + 1);
  };

  const handleRestartStripeCheckout = async () => {
    if (onRetryCheckout) {
      onRetryCheckout();
      return;
    }

    // Direct Stripe checkout relaunch fallback
    setIsRestartingCheckout(true);
    try {
      const effectiveUserId = userId || currentUser?.id || 'current_athlete';
      const effectiveEmail = userEmail || currentUser?.email || 'athlete@playgroundleague.pro';
      
      console.log('[StripePaymentStatusTracker] Relaunching direct Stripe Checkout creation for user:', {
        userId: effectiveUserId,
        email: effectiveEmail,
      });

      const session = await PaymentProcessor.processStripeCheckout({
        userId: effectiveUserId,
        userEmail: effectiveEmail,
        planType: 'monthly_pro',
        amount: 9.99,
      });

      if (session && session.url) {
        const absoluteUrl = new URL(session.url, window.location.origin).href;
        console.log('[StripePaymentStatusTracker] Direct redirecting to new Stripe Checkout session:', absoluteUrl);
        window.location.assign(absoluteUrl);
      }
    } catch (err: any) {
      console.error('[StripePaymentStatusTracker] Failed to re-trigger checkout session:', err);
      setErrorMessage(`Failed to start new checkout session: ${err?.message || 'Please check your connection.'}`);
      setIsRestartingCheckout(false);
    }
  };

  const handleContinueToDashboard = () => {
    if (verificationData) {
      onVerified({
        isPro: true,
        isVerifiedPro: true,
        subscriptionTier: 'pro',
        paymentReceiptId: verificationData.receiptId,
        paymentMethod: 'stripe',
        paymentDate: new Date().toISOString(),
      });
    }
    try {
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (e) {}
    onClose();
  };

  const displaySessionId = activeSessionId ? `${activeSessionId.slice(0, 18)}...` : 'Detecting...';

  return (
    <div id="stripe-payment-status-tracker-modal" className="fixed inset-0 z-[100] bg-indigo-950/90 backdrop-blur-xl flex items-center justify-center p-4 animate-fadeIn select-none">
      <div className="bg-gradient-to-b from-indigo-900/95 via-indigo-950/95 to-purple-950/95 text-white rounded-[2.5rem] border-2 border-lime-400/50 shadow-2xl w-full max-w-xl overflow-hidden relative p-6 sm:p-8 space-y-6">
        
        {/* Glow ambient background effect */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-lime-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />

        {/* Header */}
        <div className="text-center space-y-2 relative z-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-gradient-to-tr from-amber-400 via-yellow-300 to-lime-400 text-black shadow-xl shadow-lime-400/20 mx-auto animate-bounce-short">
            <Crown className="w-9 h-9 stroke-[2.5]" />
          </div>

          <div className="inline-block px-3 py-1 bg-lime-400/20 border border-lime-400/40 rounded-full text-[10px] font-black italic uppercase text-lime-300 tracking-wider">
            Stripe Payment Verification Gateway
          </div>

          <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight text-white">
            {currentStep === 'completed' 
              ? 'Welcome to Playground PRO!' 
              : currentStep === 'failed' 
              ? 'Verification Status Notice' 
              : 'Verifying Stripe Payment...'}
          </h2>

          <p className="text-xs sm:text-sm text-indigo-200/80 font-medium max-w-md mx-auto">
            {currentStep === 'completed'
              ? 'Your payment session is confirmed and your athlete profile is upgraded with Gold Verified PRO status.'
              : currentStep === 'failed'
              ? 'We were unable to confirm payment completion immediately. You can re-poll the gateway or restart Stripe Checkout.'
              : 'Please wait while we establish a secure cryptographic handshake with Stripe and synchronize your Firestore records.'}
          </p>
        </div>

        {/* Live Descriptive Status Feed */}
        {currentStep !== 'completed' && currentStep !== 'failed' && (
          <div className="bg-indigo-950/90 border border-lime-400/30 rounded-2xl p-3.5 flex items-center space-x-3 text-xs text-lime-300 font-mono shadow-inner animate-pulse">
            <Activity className="w-4 h-4 text-lime-400 shrink-0 animate-spin" />
            <div className="flex-1 overflow-hidden truncate">
              <span className="text-white/60 uppercase font-bold text-[10px] mr-2">LIVE STATUS:</span>
              <span className="text-lime-200 font-semibold">{stepDetailMessage}</span>
            </div>
          </div>
        )}

        {/* Animated Progress Bar */}
        <div className="space-y-2 relative z-10">
          <div className="flex justify-between text-[11px] font-bold uppercase tracking-wider text-indigo-200">
            <span className="flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-lime-400" />
              <span>Cryptographic Verification Pipeline</span>
            </span>
            <span className="text-lime-400 font-mono">{progressPercent}%</span>
          </div>
          <div className="w-full h-3 bg-indigo-950/80 rounded-full border border-white/10 overflow-hidden p-0.5 shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-amber-400 via-lime-400 to-emerald-400 rounded-full transition-all duration-700 ease-out shadow-lg shadow-lime-400/30"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Verification Step Items */}
        <div className="bg-indigo-950/70 border border-white/10 rounded-2xl p-4 space-y-3 relative z-10">
          {/* Step 1: Connecting to Gateway */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-3">
              {currentStep === 'connecting' ? (
                <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-lime-400" />
              )}
              <span className={currentStep === 'connecting' ? 'text-amber-300 font-bold' : 'text-indigo-100 font-semibold'}>
                1. Connecting to Stripe Payment Gateway
              </span>
            </div>
            <span className="text-[10px] uppercase font-bold text-lime-400 font-mono">
              {currentStep === 'connecting' ? 'Handshake...' : 'Connected'}
            </span>
          </div>

          {/* Step 2: Verifying Session */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-3">
              {currentStep === 'connecting' ? (
                <div className="w-4 h-4 rounded-full border border-white/20" />
              ) : currentStep === 'verifying_session' ? (
                <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
              ) : currentStep === 'failed' ? (
                <AlertTriangle className="w-4 h-4 text-rose-400" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-lime-400" />
              )}
              <span className={currentStep === 'verifying_session' ? 'text-amber-300 font-bold' : 'text-indigo-100 font-semibold'}>
                2. Cryptographic Checkout Verification ({displaySessionId})
              </span>
            </div>
            <span className="text-[10px] uppercase font-bold text-lime-400 font-mono">
              {currentStep === 'connecting' 
                ? 'Pending' 
                : currentStep === 'verifying_session' 
                ? `Polling (${pollAttempt + 1}/3)...` 
                : currentStep === 'failed' 
                ? 'Unconfirmed' 
                : 'Valid'}
            </span>
          </div>

          {/* Step 3: Syncing Firestore */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-3">
              {currentStep === 'connecting' || currentStep === 'verifying_session' ? (
                <div className="w-4 h-4 rounded-full border border-white/20" />
              ) : currentStep === 'syncing_firestore' ? (
                <Loader2 className="w-4 h-4 text-lime-400 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-lime-400" />
              )}
              <span className={currentStep === 'syncing_firestore' ? 'text-lime-300 font-bold' : 'text-indigo-100 font-semibold'}>
                3. Synchronizing Firestore Athlete Document (`isPro: true`)
              </span>
            </div>
            <span className="text-[10px] uppercase font-bold text-lime-400 font-mono">
              {currentStep === 'completed' ? 'Synchronized' : currentStep === 'syncing_firestore' ? 'Writing...' : 'Pending'}
            </span>
          </div>

          {/* Step 4: Membership Activation */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-3">
              {currentStep === 'completed' ? (
                <Crown className="w-4 h-4 text-amber-400" />
              ) : (
                <div className="w-4 h-4 rounded-full border border-white/20" />
              )}
              <span className={currentStep === 'completed' ? 'text-amber-300 font-bold' : 'text-indigo-300'}>
                4. Lifetime / Monthly PRO Membership Activated
              </span>
            </div>
            <span className="text-[10px] uppercase font-bold text-amber-400 font-mono">
              {currentStep === 'completed' ? 'Active 👑' : 'Locked'}
            </span>
          </div>
        </div>

        {/* User-Friendly Error UI within the modal if verification fails or returns non-success */}
        {currentStep === 'failed' && (
          <div id="stripe-status-tracker-error-banner" className="bg-rose-500/20 border-2 border-rose-500/50 rounded-2xl p-5 space-y-4 text-xs text-rose-200 animate-fadeIn">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-rose-300 font-black uppercase text-[12px] font-mono">
                <AlertTriangle className="w-4 h-4 text-rose-400" />
                <span>Verification Not Yet Confirmed</span>
              </div>
              <span className="text-[10px] text-rose-300/80 font-mono px-2 py-0.5 bg-rose-950/60 rounded">Attempt {pollAttempt + 1}</span>
            </div>

            <p className="text-rose-100 leading-relaxed font-sans text-xs">
              {errorMessage || 'Stripe may still be processing your transaction, or checkout was closed before completion. If your payment was completed, click Retry to re-query the gateway, or click Restart Checkout to trigger a fresh session.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                id="stripe-tracker-retry-btn"
                type="button"
                onClick={handleRetryVerification}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-400 hover:to-amber-400 text-white font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center justify-center space-x-2 active:scale-95"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Retry Verification</span>
              </button>

              <button
                id="stripe-tracker-relaunch-btn"
                type="button"
                disabled={isRestartingCheckout}
                onClick={handleRestartStripeCheckout}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 text-black font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center justify-center space-x-2 active:scale-95"
              >
                {isRestartingCheckout ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Launching Stripe...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    <span>Restart Stripe Checkout</span>
                  </>
                )}
              </button>

              <button
                id="stripe-tracker-dismiss-btn"
                type="button"
                onClick={handleContinueToDashboard}
                className="py-3 px-4 bg-indigo-950 hover:bg-indigo-900 text-indigo-200 font-bold rounded-xl border border-white/15 transition text-center"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Verification Success Details */}
        {currentStep === 'completed' && verificationData && (
          <div className="space-y-4 animate-fadeIn relative z-10">
            {/* Unlocked Privileges Bento */}
            <div className="grid grid-cols-2 gap-2 text-left">
              <div className="p-3 bg-amber-400/10 border border-amber-400/30 rounded-2xl flex items-center space-x-2.5">
                <Crown className="w-5 h-5 text-amber-400 shrink-0" />
                <div>
                  <div className="text-[11px] font-black text-amber-300 uppercase">Gold Verified Badge</div>
                  <div className="text-[10px] text-indigo-200">Active on all leaderboards</div>
                </div>
              </div>

              <div className="p-3 bg-lime-400/10 border border-lime-400/30 rounded-2xl flex items-center space-x-2.5">
                <Zap className="w-5 h-5 text-lime-400 shrink-0" />
                <div>
                  <div className="text-[11px] font-black text-lime-300 uppercase">1080p Highlights</div>
                  <div className="text-[10px] text-indigo-200">Unlimited cloud uploads</div>
                </div>
              </div>
            </div>

            {/* Receipt and Hash Meta */}
            <div className="p-3.5 bg-indigo-950/80 border border-white/10 rounded-2xl space-y-1.5 text-left text-[11px]">
              <div className="flex justify-between items-center text-indigo-300">
                <span>Receipt Number:</span>
                <span className="font-mono text-lime-300 font-bold">{verificationData.receiptId}</span>
              </div>
              <div className="flex justify-between items-center text-indigo-300">
                <span>Payment Method:</span>
                <span className="text-white font-medium">Stripe Verified Gateway</span>
              </div>
              <div className="flex justify-between items-center text-indigo-300">
                <span>Account Status:</span>
                <span className="text-lime-400 font-bold uppercase">isPro = true</span>
              </div>
            </div>

            {/* Action CTA Button */}
            <button
              id="stripe-tracker-continue-btn"
              type="button"
              onClick={handleContinueToDashboard}
              className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-lime-400 via-emerald-400 to-teal-400 hover:from-lime-300 hover:to-teal-300 text-black font-black italic uppercase text-sm tracking-wide shadow-xl shadow-lime-400/20 transition transform active:scale-95 flex items-center justify-center space-x-2"
            >
              <span>Continue to PRO Dashboard</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {currentStep !== 'completed' && currentStep !== 'failed' && (
          <div className="text-center text-[11px] text-indigo-300/70 font-semibold flex items-center justify-center space-x-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-lime-400" />
            <span>Secure 256-bit SSL handshake in progress...</span>
          </div>
        )}

      </div>
    </div>
  );
};
